import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { PaymentDeviceRentalRequestStatus, PaymentDeviceRentalStatus } from '@/models/common/PaymentDeviceEnums';
import { FundPaymentDevicesDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { fundDevicesManagementUrl, isAwaitingPickup } from '@/helpers/paymentDevices';
import addIcon from '@/assets/icons/add.svg';
import { useDeviceDialogs, useRequestDialogs } from '../../PaymentDevices/useDeviceDialogs';
import { RentalRow, RequestRow } from '../../PaymentDevices/RentalRows';
import RentalHistoryTable from '../../PaymentDevices/RentalHistoryTable';
import styles from './FundBlade.module.css';

interface DevicesTabProps {
  fundId: number;
  fundName: string;
}

/**
 * The terminals a fund holds, the ones it held, and its rental requests (HAT-1434) - with the
 * same actions as the inventory, so the office can handle a fund's terminals from its card.
 */
const DevicesTab = ({ fundId, fundName }: DevicesTabProps) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetAuthApi<FundPaymentDevicesDtoIn>(fundDevicesManagementUrl(fundId));
  const deviceDialogs = useDeviceDialogs();
  const requestDialogs = useRequestDialogs();

  const actionButton = (label: string, onClick: () => void) => (
    <button type="button" className={styles.rowEditBtn} onClick={onClick}>{label}</button>
  );

  const section = (title: string, empty: string, items: ReactNode[]) => (
    <section className="flex flex-col gap-3">
      <h3 className={styles.sectionTitle}>{title}</h3>
      {items.length === 0 ? <p className={styles.emptyText}>{empty}</p> : <div className={styles.personList}>{items}</div>}
    </section>
  );

  return (
    <div className="flex flex-col gap-6">
      <div className={styles.tabToolbar}>
        <button type="button" className={styles.editIconBtn} onClick={() => deviceDialogs.openAssign({ device: null, fundId, fundName })}>
          <img src={addIcon} alt="" />
          {t('paymentDevices.fundTab.assign')}
        </button>
      </div>

      {isLoading || !data ? (
        <p className={styles.emptyText}>{t('loading')}...</p>
      ) : (
        <>
          {section(t('paymentDevices.fundTab.current'), t('paymentDevices.fundTab.noCurrent'), data.current.map((rental) => {
            const target = { deviceId: rental.deviceId, deviceNumber: rental.deviceNumber, rental };
            return (
              <RentalRow
                key={rental.id}
                rental={rental}
                heading={<span dir="ltr">{t('paymentDevices.fundTab.device', { number: rental.deviceNumber })}</span>}
                actions={rental.status === PaymentDeviceRentalStatus.Reserved ? (
                  <>
                    {actionButton(t('paymentDevices.actions.handOver'), () => deviceDialogs.runOnTarget('handOver', target))}
                    {actionButton(t('paymentDevices.actions.cancelAssignment'), () => deviceDialogs.runOnTarget('cancelAssignment', target))}
                  </>
                ) : (
                  <>
                    {actionButton(t('paymentDevices.actions.editHandOverDate'), () => deviceDialogs.runOnTarget('editHandOverDate', target))}
                    {actionButton(t('paymentDevices.actions.return'), () => deviceDialogs.runOnTarget('return', target))}
                  </>
                )}
              />
            );
          }))}

          <div className={styles.line} />

          {section(t('paymentDevices.fundTab.requests'), t('paymentDevices.fundTab.noRequests'), data.requests.map((request) => (
            <RequestRow
              key={request.id}
              request={request}
              actions={request.status === PaymentDeviceRentalRequestStatus.Pending ? (
                <>
                  {actionButton(t('paymentDevices.actions.approve'), () => requestDialogs.approve(request))}
                  {actionButton(t('paymentDevices.actions.reject'), () => requestDialogs.reject(request))}
                </>
              ) : isAwaitingPickup(request) ? (
                actionButton(t('paymentDevices.actions.handOver'), () => requestDialogs.collect(request))
              ) : request.status === PaymentDeviceRentalRequestStatus.Cancelled ? (
                actionButton(t('paymentDevices.actions.reopen'), () => requestDialogs.reopen(request))
              ) : undefined}
            />
          )))}

          <div className={styles.line} />

          <section className="flex flex-col gap-3">
            <h3 className={styles.sectionTitle}>{t('paymentDevices.fundTab.history')}</h3>
            {data.history.length === 0
              ? <p className={styles.emptyText}>{t('paymentDevices.fundTab.noHistory')}</p>
              : <RentalHistoryTable rentals={data.history} by="device" />}
          </section>
        </>
      )}

      {deviceDialogs.dialogs}
      {requestDialogs.dialogs}
    </div>
  );
};

export default DevicesTab;
